import type { EReasoningMode, IDownloadProgressEvent, IMessage } from "./chat";
import type { TEntityId } from "./entity-id";
import type { IPiiEntityTypeCount, IPiiSpan } from "./pii-span";

// NOTE: The six categories the server stores in `messages.flag_category`
// (server EFlagCategory). A flagged message can also carry none — see
// UNSAFE_FLAG_STATUS.
export enum EAnalyticsFlagCategory {
  CONTENT_LEAKAGE = "content_leakage",
  POLICY_VIOLATION = "policy_violation",
  JAILBREAK = "jailbreak",
  OFF_TOPIC = "off_topic",
  MISINFORMATION = "misinformation",
  SECURITY_RISK = "security_risk",
}

// NOTE: NSAI-5967 — the message fields getMessageStatusKey reads.
export interface IAnalyticsMessageStatusSource {
  flagged: boolean;
  flagCategory: string | null;
  // NOTE: NSAI-6408 — set on a Messages row; an automation run is never
  // screened, so its label is "Not screened" rather than "Safe".
  messageSource?: EAnalyticsMessageSource | undefined;
  automationRun?: IAnalyticsAutomationRunSummary | null | undefined;
}

export interface IAnalyticsResponse {
  flaggedPercentage: {
    totalMessages: number;
    flaggedMessages: number;
    percentage: number;
  };
  categoryDistribution: Array<{
    category: string;
    count: number;
    percentage: number;
  }>;
  flaggedVsSafe: {
    flagged: number;
    safe: number;
    flaggedPercentage: number;
    safePercentage: number;
  };
  messagesByDirection: Array<{
    direction: string;
    flagged: number;
    total: number;
    percentage: number;
  }>;
  topUsers: Array<{
    userId: TEntityId;
    // NOTE: NSAI-8013 — matched to a project member to open their violations.
    userUuid?: string;
    userFirstName?: string;
    userLastName?: string;
    userEmail?: string;
    flaggedCount: number;
  }>;
  totalAnalyzedMessages: number;
  dateRange: {
    from: Date;
    to: Date;
  };
  dailyStats: Array<{
    date: string;
    totalMessages: number;
    flaggedMessages: number;
    safeMessages: number;
    flaggedPercentage: number;
    threatsFlagged: number;
  }>;
  dailyFlaggedVsSafe: Array<{
    date: string;
    flagged: number;
    safe: number;
  }>;
}

export interface IMessagesSummary {
  totalMessages: number;
  flaggedMessages: number;
  flaggedPercentage: number;
  lastAnalyzedAt: Date | null;
}

export enum EAnalyticsMessageSource {
  EXTERNAL_API = "external_api",
  // NOTE: NSAI-7030 — a probe verdict on an agent or workflow run. NSAI-6408 —
  // the same source also marks a scheduled task, webhook, hook or workflow run
  // row. That row is not a chat message: chatUuid is null and it carries
  // `automationRun` instead.
  AUTOMATION = "automation",
  CHAT_UI = "chat_ui",
  ASSISTANT_PREVIEW = "assistant_preview",
  TELEGRAM = "telegram",
  TEAMS = "teams",
  SLACK = "slack",
  WHATSAPP = "whatsapp",
  MATRIX = "matrix",
}

// NOTE: NSAI-6408 — which kind of automation produced a run row.
export enum EAnalyticsAutomationType {
  CRON = "cron",
  WEBHOOK = "webhook",
  HOOK = "hook",
  WORKFLOW = "workflow",
}

export interface IAnalyticsAutomationRunSummary {
  automationType: EAnalyticsAutomationType;
  automationUuid: string;
  automationName: string | null;
  status: string;
  triggerType: string | null;
}

// NOTE: NSAI-5707 — one turn's retrieval-screening outcome, aggregated across
// the knowledge-base and chat/assistant-file retrieval paths.
export interface IRetrievalScreening {
  // NOTE: How many chunks screening looked at. Carried alongside the drop count
  // because "2 of 8 removed" and "2 of 2 removed" say very different things
  // about whether a probe is over-matching.
  inspectedCount: number;
  droppedCount: number;
  drops: IRetrievalScreeningDrop[];
}

export interface IRetrievalScreeningDrop {
  // NOTE: The document the chunk came from. Null when the source could not be
  // labelled (a chunk with no file name in its metadata).
  source: string | null;
  // NOTE: Null on the two drop paths where no probe matched: a fail-closed drop
  // (screening could not run) and an overflow drop (more chunks than the rails
  // inspect in one call). The reason field says which.
  probeUuid: string | null;
  probeName: string | null;
  matchedBehavior: string | null;
  category: string | null;
  reason: string | null;
}

export interface IAnalyticsMessage {
  uuid: string;
  role: string;
  model: string | null;
  provider: string | null;
  direction: "input" | "output";
  flagged: boolean;
  flagCategory: string | null;
  flagDetails: string | null;
  // NOTE: NSAI-5707 — what RAG retrieval screening removed from this turn.
  // The flag fields above are the MESSAGE-level verdict; this is the
  // chunk-level one, and it had no administrator-facing surface at all before:
  // a probe stripping legitimate knowledge left only a Token Usage cost row, so
  // it looked identical to a knowledge base with no answer. Optional on the
  // wire (older fixtures/mocks predate it); null or absent means retrieval
  // screening removed nothing.
  retrievalScreening?: IRetrievalScreening | null;
  probeUuid: string | null;
  // NOTE: NSAI-6408 — null on an automation-run row, which has no chat.
  chatUuid: string | null;
  // NOTE: NSAI-6408 — set only on an automation-run row. Optional on the wire
  // so older fixtures stay valid; null or absent means a chat message.
  automationRun?: IAnalyticsAutomationRunSummary | null;
  // NOTE: Used by the message-detail modal so the follow-up context fetch
  // targets the row's actual project (matters in org-wide scope where rows
  // can come from different projects than the ambient X-Project-Uuid).
  projectUuid: string;
  messageSource: EAnalyticsMessageSource;
  assistantName?: string | null;
  isAssistantDeleted?: boolean;
  // NOTE: True when the row's (single) model was soft-deleted. Optional on the
  // wire to keep older fixtures/mocks valid; the UI keeps showing the model
  // name with a "(deleted)" marker instead of collapsing to "-".
  isModelDeleted?: boolean;
  isMultiModel: boolean;
  modelNames: string | null;
  providerNames: string | null;
  userFirstName: string | null;
  userLastName: string | null;
  userEmail: string | null;
  createdAt: string;
  // NOTE: Optional on the wire to keep older fixtures/mocks valid; backend
  // always emits this (null when the row is not deleted).
  deletedAt?: string | null;
  // NOTE: Only present when the message was sent using a reasoning-capable
  // model and the user had the reasoning mode selector visible. Undefined
  // means the model did not offer a reasoning mode choice.
  reasoningMode?: EReasoningMode | null;
}

export enum EAnalyticsScope {
  ORG = "org",
}

// NOTE: Optional scope override for the Messages-tab endpoints. When neither
// is set the request is scoped to the X-Project-Uuid header (today's
// behavior). projectUuid switches the analytics query to a different
// project; scope=org switches to org-wide and is owner-only on the server.
export interface IAnalyticsScopeParams {
  projectUuid?: string;
  scope?: EAnalyticsScope;
}

export interface IAnalyticsQueryParams extends IAnalyticsScopeParams {
  hasAllMembersAnalyticsReadPermission: boolean;
  dateFrom?: string;
  dateTo?: string;
  // NOTE: Empty array / undefined ⇒ no member filter. Only forwarded on the
  // all-members route, gated by ALL_MEMBERS_ANALYTICS_READ.
  memberUuids?: string[];
  includeDeleted?: boolean;
}

export interface IRecentFlaggedMessagesParams extends IAnalyticsQueryParams {
  limit?: number;
}

export enum EAnalyticsMessageSortColumn {
  CREATED_AT = "createdAt",
  DIRECTION = "direction",
  MODEL = "model",
  PROVIDER = "provider",
  FLAG_CATEGORY = "flagCategory",
}

export type TAnalyticsSortOrder = "ASC" | "DESC";

// NOTE: Messages-list endpoints use a tri-state `deletionStatus` enum instead
// of the binary `includeDeleted` shared by the other analytics endpoints.
// Omitting it means "both live and deleted" — today's default view.
export enum EDeletionStatus {
  LIVE = "live",
  DELETED = "deleted",
}

// NOTE: Category keys for the Security Analytics → Messages filter UI. Shared
// by web (FilterDropdown) and mobile (FilterBottomSheet) so the categories
// array, filterValues object, multi-setter map, and chip-dispatch switch all
// reference the same source of truth.
export enum EMessagesFilterKey {
  MEMBER = "member",
  DIRECTION = "direction",
  SOURCE = "source",
  STATUS = "status",
  DELETION_STATUS = "deletionStatus",
  MODEL = "model",
  PROVIDER = "provider",
  DATE_RANGE = "dateRange",
}

// NOTE: Wire value sent on the messages-list direction filter. Mirrors the
// backend's EMessageDirection enum without pulling server code in.
export enum EMessageDirection {
  INPUT = "input",
  OUTPUT = "output",
}

export interface IAnalyticsMessagesParams extends Omit<
  IAnalyticsQueryParams,
  "includeDeleted"
> {
  messageUuid?: string;
  limit?: number;
  offset?: number;
  sortColumn?: EAnalyticsMessageSortColumn;
  sortOrder?: TAnalyticsSortOrder;
  direction?: EMessageDirection;
  source?: string[];
  status?: string[];
  modelTitle?: string[];
  providerName?: string[];
  assistantName?: string[];
  deletionStatus?: EDeletionStatus;
  // NOTE: Free-text search over message content, matched server-side by a
  // decrypt-and-scan. PII-anonymized content is searched as stored, so terms
  // replaced by placeholders in anonymized projects won't match.
  search?: string;
}

// NOTE: NSAI-7022 — mirrors server EAnalyticsExportFormat.
export type TAnalyticsExportFormat = "csv" | "xlsx";

// NOTE: NSAI-7022 — the Messages list's filters plus a file format. Paging and
// the single-message deep link do not apply: an export starts at the first row
// and the server caps it.
export interface IExportAnalyticsMessagesParams extends Omit<
  IAnalyticsMessagesParams,
  "limit" | "offset" | "messageUuid"
> {
  format: TAnalyticsExportFormat;
}

export interface IFormatModelDisplayProps {
  isMultiModel: boolean;
  model: string | null;
  provider: string | null;
  modelNames: string | null;
  providerNames: string | null;
}

export interface IPaginatedAnalyticsMessagesResponse {
  data: IAnalyticsMessage[];
  total: number;
  hasNextPage: boolean;
  // NOTE: Set when a content search hit the scan hard cap — the page is valid
  // but incomplete, so the UI prompts for tighter filters.
  isSearchTruncated?: boolean;
}

export interface IMessageModelAndProviderOptionsResponse {
  models: string[];
  providers: string[];
  assistants: string[];
}

export interface IMessageContextParams {
  chatUuid: string;
  messageUuid: string;
  projectUuid?: string;
  includeDeleted?: boolean;
}

export type TMessageContextResponse = IMessage[];

export interface IAutomationRunContextParams {
  runUuid: string;
  automationType: EAnalyticsAutomationType;
  projectUuid?: string;
}

export interface IAnalyticsAutomationRunStep {
  stepOrder: number;
  stepType: string;
  status: string;
  input: string | null;
  output: string | null;
  error: string | null;
}

// NOTE: NSAI-6408 — one run as the reviewer reads it: the instructions sent to
// the model, what the trigger fed it and what came back, as stored (automation
// text is persisted in the clear, so there is nothing to restore). Owner, model
// and project come from the Messages row, not from here.
export interface IAnalyticsAutomationRunContext {
  runUuid: string;
  automationType: EAnalyticsAutomationType;
  automationName: string | null;
  status: string;
  triggerType: string | null;
  prompt: string | null;
  // NOTE: True when `prompt` is the copy the run kept of what it was sent;
  // false when the run predates that copy and `prompt` is the automation's
  // current instructions, which a later edit may have changed.
  isPromptAsSent: boolean;
  triggerPayload: string | null;
  output: string | null;
  error: string | null;
  steps: IAnalyticsAutomationRunStep[];
  finishedAt: string | null;
}

export interface IAutomationRunPromptLabels {
  label: string;
  hint?: string;
}

export enum EThreadWindowDirection {
  BEFORE = "before",
  AFTER = "after",
}

// NOTE: Opaque pagination cursor for the Security Analytics thread drawer —
// the boundary turn root's createdAt (ISO) + uuid tiebreaker.
export interface IThreadWindowCursor {
  createdAt: string;
  uuid: string;
}

export interface IChatThreadWindowParams {
  chatUuid: string;
  anchorMessageUuid: string;
  projectUuid?: string;
  limit?: number;
  direction?: EThreadWindowDirection;
  cursorCreatedAt?: string;
  cursorUuid?: string;
  includeDeleted?: boolean;
}

export interface IChatThreadWindowResponse {
  messages: IMessage[];
  chatTitle: string;
  isChatDeleted: boolean;
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  beforeCursor: IThreadWindowCursor | null;
  afterCursor: IThreadWindowCursor | null;
}

// NOTE: Cross-project file download from the Security Analytics Message
// Details modal. The row's projectUuid (or scope=org) tells the server which
// project owns the file so it can re-enter RLS context appropriately —
// independent of the caller's ambient X-Project-Uuid header.
export interface IDownloadAnalyticsMessageFileParams extends IAnalyticsScopeParams {
  fileUuid: string;
  onProgress?: (event: IDownloadProgressEvent) => void;
}

// NOTE: Cross-project file preview from Security Analytics. Same scope
// resolution as download but hits the Gotenberg preview endpoint.
export interface IPreviewAnalyticsMessageFileParams extends IAnalyticsScopeParams {
  fileUuid: string;
}

// NOTE: NSAI-6839 — how an uploaded attachment was anonymized. Mirrors
// `EMessageFileAnonymizationStatus` in
// `apps/nsai/server/src/analytics/analytics.types.ts`.
export enum EMessageFileAnonymizationStatus {
  ANONYMIZED = "anonymized",
  NOT_MASKED = "not_masked",
  NO_TEXT_CONTENT = "no_text_content",
}

// NOTE: NSAI-6839 — which text the document-anonymization view shows: the
// anonymized text (masked values drawn as their placeholders, the form in which
// any part of the document reaches the model) or the original (restored
// values, each revealing the placeholder it travelled as).
export enum EDocumentAnonymizationViewMode {
  SENT = "sent",
  ORIGINAL = "original",
}

export interface IMessageFileAnonymizationParams extends IAnalyticsScopeParams {
  fileUuid: string;
}

// NOTE: NSAI-6839 — `content` is the RESTORED document text (what the user
// uploaded) and `piiSpans` locate the values that reached the model as
// placeholders, with the same contract as a message's spans. The server caps
// `content`; `isTruncated` says the tail was cut and `totalLength` how long the
// whole text is. `maskedValueCount` and `entityTypeCounts` cover the whole
// document, including a tail the cap left out.
export interface IMessageFileAnonymization {
  fileUuid: string;
  fileName: string;
  mimeType: string;
  status: EMessageFileAnonymizationStatus;
  content: string | null;
  piiSpans: IPiiSpan[];
  maskedValueCount: number;
  entityTypeCounts: IPiiEntityTypeCount[];
  totalLength: number;
  isTruncated: boolean;
}

// NOTE: NSAI-6895 — the slice of a thread message `isMediaGenerationTurn`
// reads; web's analytics context and thread window both pass `IMessage`.
export type TMediaGenerationTurnMessage = Pick<
  IMessage,
  "uuid" | "role" | "parentId" | "generationType"
>;

export interface IIsMediaGenerationTurnProps {
  messages: TMediaGenerationTurnMessage[] | undefined;
  messageUuid: string;
}
